import { useCallback, useMemo, useState } from 'react';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import * as api from '../api';
import PageHeader from '../components/ui/PageHeader';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import FormField from '../components/ui/FormField';
import ErrorMessage from '../components/ui/ErrorMessage';
import { formatDateTime, toDateInputValue } from '../utils/formatDate';
import { usePaginatedList } from '../hooks/usePaginatedList';
import './CrudPage.scss';

const DISCOUNT_TYPE_OPTIONS = [
    { value: 'percent', label: 'Процент (%)' },
    { value: 'fixed', label: 'Фиксированная сумма (₽)' },
];

const DISCOUNT_TYPE_LABELS = Object.fromEntries(
    DISCOUNT_TYPE_OPTIONS.map(({ value, label }) => [value, label]),
);

function emptyForm() {
    return {
        code: '',
        discount_type: 'percent',
        discount_value: '10',
        active: true,
        expires_at: '',
        max_uses: '',
    };
}

export default function PromoCodesPage() {
    const fetchList = useCallback(
        (params) => api.listResource('promo_codes', params),
        [],
    );
    const {
        items, total, offset, limit, loading, error, setOffset, reload,
    } = usePaginatedList(fetchList);

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setFormError('');
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            code: (row.code || '').toUpperCase(),
            discount_type: row.discount_type || 'percent',
            discount_value: String(row.discount_value ?? ''),
            active: Boolean(row.active),
            expires_at: toDateInputValue(row.expires_at),
            max_uses: row.max_uses != null ? String(row.max_uses) : '',
        });
        setFormError('');
        setModalOpen(true);
    };

    const buildBody = () => ({
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: form.discount_value,
        active: form.active,
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
    });

    const handleSave = async () => {
        setSaving(true);
        setFormError('');
        try {
            const body = buildBody();
            if (editing) {
                const patch = {};
                Object.entries(body).forEach(([key, value]) => {
                    if (value !== editing[key]) patch[key] = value;
                });
                await api.updateResource('promo_codes', editing.id, patch);
            } else {
                await api.createResource('promo_codes', body);
            }
            setModalOpen(false);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.deleteResource('promo_codes', deleteTarget.id);
            setDeleteTarget(null);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка удаления');
        } finally {
            setDeleting(false);
        }
    };

    const columns = useMemo(() => [
        { key: 'id', title: 'ID', width: 60 },
        { key: 'code', title: 'Код' },
        {
            key: 'discount_type',
            title: 'Тип',
            render: (v) => DISCOUNT_TYPE_LABELS[v] || v,
        },
        { key: 'discount_value', title: 'Скидка' },
        {
            key: 'active',
            title: 'Активен',
            render: (v) => (v ? 'Да' : 'Нет'),
        },
        { key: 'used_count', title: 'Использовано' },
        { key: 'max_uses', title: 'Лимит' },
        {
            key: 'expires_at',
            title: 'Истекает',
            render: (v) => formatDateTime(v),
        },
        {
            key: 'actions',
            title: '',
            render: (_, row) => (
                <div className="cell-actions">
                    <button type="button" className="btn btn--outline btn--sm" onClick={() => openEdit(row)}>
                        <IconEdit size={16} />
                    </button>
                    <button type="button" className="btn btn--danger btn--sm" onClick={() => setDeleteTarget(row)}>
                        <IconTrash size={16} />
                    </button>
                </div>
            ),
        },
    ], []);

    return (
        <div className="crud-page">
            <PageHeader
                title="Промокоды"
                subtitle="Скидки и акции"
                actions={(
                    <button type="button" className="btn btn--primary btn--sm" onClick={openCreate}>
                        <IconPlus size={16} />
                        Добавить
                    </button>
                )}
            />

            <ErrorMessage message={error} onRetry={reload} />

            <DataTable
                columns={columns}
                rows={items}
                loading={loading}
                pagination={{ total, limit, offset, onPageChange: setOffset }}
            />

            <Modal
                open={modalOpen}
                title={editing ? `Промокод #${editing.id}` : 'Новый промокод'}
                onClose={() => setModalOpen(false)}
                footer={(
                    <>
                        <button type="button" className="btn btn--outline btn--sm" onClick={() => setModalOpen(false)}>Отмена</button>
                        <button type="button" className="btn btn--primary btn--sm" onClick={handleSave} disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                    </>
                )}
            >
                <ErrorMessage message={formError} />
                <div className="form-grid">
                    <FormField
                        label="Код"
                        value={form.code}
                        onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                        autoCapitalize="characters"
                        spellCheck={false}
                    />
                    <FormField
                        label="Тип скидки"
                        value={form.discount_type}
                        onChange={(e) => setForm((p) => ({ ...p, discount_type: e.target.value }))}
                        options={DISCOUNT_TYPE_OPTIONS}
                    />
                    <FormField label="Значение" value={form.discount_value} onChange={(e) => setForm((p) => ({ ...p, discount_value: e.target.value }))} />
                    <FormField label="Истекает" type="date" value={form.expires_at} onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))} />
                    <FormField label="Макс. использований" type="number" value={form.max_uses} onChange={(e) => setForm((p) => ({ ...p, max_uses: e.target.value }))} />
                </div>
                <label className="form-check">
                    <input type="checkbox" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
                    Активен
                </label>
            </Modal>

            <ConfirmModal
                open={Boolean(deleteTarget)}
                title="Удалить промокод?"
                message={`Промокод ${deleteTarget?.code || ''} будет удалён.`}
                confirmLabel="Удалить"
                danger
                loading={deleting}
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    );
}
